<template>
  <div>
    <filter-panel
      class="filters-panel"
      @update="applyFilter"
      :apply-filter-immediately="false"
      :track-filter-settings="false"
      :filter-parameters="formattedFilters"
      :notification-text="null"
      :api-request-data="apiRequestData">

      <template #panel-actions>
        <div class="actions">
          <b-button @click="vesselsMapModal.show = true"
                    class="action-button"
                    size="sm"
                    variant="info">
            <span class="text-nowrap">
              Live map
            </span>
          </b-button>
        </div>
      </template>
    </filter-panel>

    <vessels-map-modal
      v-model="vesselsMapModal.show"
      :required-filter-fields="mapRequiredParameters"
      :filter-values="mapAPIFilterValues"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FilterPanel from '../widgets/FilterPanel';

import {
  getCurrentFilterValues,
  getCurrentAPIFilterValues,
  getAPIFilterValues,
  applyFilterValues,
  getFormattedFilters,
} from '../widgets/FilterPanel';

import VesselsMapModal from '@/components/widgets/VesselsMapModal';
import getNested from 'lodash-es/get';


export default {
  name: 'congestion-filters-old',

  components: {
    VesselsMapModal,
    FilterPanel,
  },

  props: {
    section: {
      type: String,
      default: () => 'port'
    },

    apiRequestData: {
      type: Object,
      default: () => ({
        // it's possible to have a few active requests at the same page
        emptySection: {
          method: 'GET',
          url: '',
          headers: {},
          params: {},
          data: {},
        },
      })
    },
  },

  data() {
    const segmentChoices = [
      {
        'text': 'Capesize',
        'value': 'capesize',
      },
      {
        'text': 'Handysize',
        'value': 'handysize',
      },
      {
        'text': 'Panamax',
        'value': 'panamax',
      },
      {
        'text': 'Supramax',
        'value': 'supramax',
      },
    ];

    const defaultChoices = {
      country: [{text: '-- Country --', value: null}],
      port: [{text: '-- Port --', value: null}],
      region: [{text: '-- Region --', value: null}],
      segment: segmentChoices,
    };

    return {
      filtersReady: false,

      filterSchema: {
        port: {
          name: 'port',
          apiName: 'portId',
          type: 'multiselect',
          placeholder: 'Port',
          value: [],
          defaultChoice: defaultChoices['port'],
          choices: [],
          options: {
            // this field will be displayed on the right of each list items
            valueLabel: 'countryCode',
          },
        },
        country: {
          name: 'country',
          apiName: 'countryCode',
          type: 'multiselect',
          placeholder: 'Country',
          value: [],
          defaultChoice: defaultChoices['country'],
          choices: [],
        },
        region: {
          name: 'region',
          apiName: 'regionId',
          type: 'multiselect-region',
          placeholder: 'Region',
          value: [],
          defaultChoice: defaultChoices['region'],
          choices: [],
        },
        segment: {
          name: 'segment',
          apiName: 'segment',
          type: 'multiselect',
          placeholder: 'Segment',
          value: segmentChoices,
          defaultChoice: defaultChoices['segment'],
          choices: segmentChoices,
        },
      },

      filtersMap: {
        port: {
          method: this.getAnchoragePorts,
          defaultValue: 2143,
          requiredFields: ['port', 'segment'],
          mapFields: ['portId', 'segment'],  // fields order should be the same as in requiredFields
        },
        country: {
          method: this.getAnchorageCountries,
          defaultValue: 'CN',
          requiredFields: ['country', 'segment'],
          mapFields: ['dischargeCountry', 'segment'],
        },
        region: {
          method: this.getAnchorageRegionsWithPolygons,
          defaultValue: 'ECSA',
          requiredFields: ['region', 'segment'],
          mapFields: ['dischargeRegion', 'segment'],
        },
      },

      vesselsMapModal: {
        show: false,
      },
    };
  },

  methods: {
    ...mapActions(
      'api/congestion', [
        'getAnchoragePorts',
        'getAnchorageCountries',
        'getAnchorageRegionsWithPolygons',
      ]
    ),

    ...mapActions('api/presets', [
      'updateCurrentFilterSettings',
    ]),

    async loadFilters() {
      await this.loadFilterChoices();
      // this.setDefaultValues();
      this.filtersReady = true;
    },

    async loadFilterChoices() {
      for(let fieldName of Object.keys(this.filtersMap)) {
        // load choices
        let choices = await this.filtersMap[fieldName].method()
        this.filterSchema[fieldName].choices = this.convertAPIDataToChoices(
          'country', choices,
        );
      }
    },

    setDefaultValues() {
      let defaultItem = null;

      for(let fieldName of Object.keys(this.filtersMap)) {
        // find a default item
        defaultItem = this.filterSchema[fieldName].choices.find(
          item => item.value === this.filtersMap[fieldName].defaultValue
        );
        // set a default item
        this.filterSchema[fieldName].value = defaultItem ? [defaultItem] : [];
      }
    },

    convertAPIDataToChoices(fieldName, data) {
      // adds default value for a select
      if(data) {
        return data;
      } else {
        return [this[fieldName].defaultChoice];
      }
    },

    applyFilter(filterValues) {
      // apply filter only when all choices are loaded
      if(!this.filtersReady) {
        return;
      }

      // apply panel filter values
      // only required fields are updated to exclude hidden sections changes
      // otherwise port/country/region could be set to null
      applyFilterValues(
        this.filterSchema,
        filterValues,
        this.filtersMap[this.section].requiredFields
      );

      this.$emit('update', getAPIFilterValues(this.filterSchema, filterValues));

      // note, we need to track all fields even if they are in hidden sections
      this.trackFilterSettingsToStore();
    },

    async trackFilterSettingsToStore() {
      await this.updateCurrentFilterSettings({
        path: this.$route.path,
        parameters: {
          filterPanel: getCurrentFilterValues(this.filterSchema)
        },
      });
    },
  },

  computed: {
    ...mapGetters(
      'api/presets', ['getActivePreset']
    ),

    formattedFilters() {
      return getFormattedFilters(
        this.filterSchema,
        this.filtersMap[this.section].requiredFields,
      );
    },

    mapAPIFilterValues() {
      const apiFilterValues = getCurrentAPIFilterValues(this.filterSchema);
      let requiredAPIFields = this.filtersMap[this.section].requiredFields.map(
        fieldName => this.filterSchema[fieldName].apiName
      );

      let mapParameters = {};
      requiredAPIFields.forEach(
        (fieldName, i) => {
          mapParameters[this.mapRequiredParameters[i]] = apiFilterValues[fieldName]
        }
      );

      return mapParameters;
    },

    mapRequiredParameters() {
      return this.filtersMap[this.section]['mapFields'];
    },
  },

  watch: {
    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        // note, a custom filter tracking is used here
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        if(!this.filtersReady) {
          await this.loadFilters();
          if(!preset) {
            this.setDefaultValues();
          }
        }

        if(preset) {
          const filterValues = getNested(preset, 'params.filterPanel', {});
          applyFilterValues(this.filterSchema, filterValues);
        }
      },
    },
  },
}
</script>


<style scoped>
.filters-panel >>> .multiselect__tags {
  min-width: 300px;
}
</style>


<style scoped lang="scss">
.actions {
  margin-top: 5px;
  display: flex;

  .action-button:not(:last-child) {
    margin-right: 5px;
  }
}
</style>
